/**
 * Composant GED DOMIUM - Affichage des documents DOMIUM dans la GED
 * 
 * Affiche les documents liés aux souscriptions DOMIUM :
 * - Conditions Particulières (CP)
 * - Conditions Générales (CG)
 * - Facture Proforma
 * - Attestation Provisoire
 * - Attestation Définitive
 * - Reçu de Paiement
 * - Documents post-souscription (PV réception, Facture acquittée)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { FileText, Download, Eye, Search, RefreshCw, FolderSync, Shield, Receipt, FileCheck, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { getToken } from '../../services/tokenService';
import { useDocumentManager } from '../../hooks/useDocumentManager';
import GEDDocumentViewerModal from './GEDDocumentViewerModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Catégories DOMIUM et leurs labels
const DOMIUM_CATEGORIES = {
  domium_cp: { label: 'Conditions Particulières', icon: FileText, color: 'bg-blue-100 text-blue-800' },
  domium_cg: { label: 'Conditions Générales', icon: FileText, color: 'bg-gray-100 text-gray-800' },
  domium_proforma: { label: 'Facture Proforma', icon: Receipt, color: 'bg-orange-100 text-orange-800' },
  domium_attestation_provisoire: { label: 'Attestation Provisoire', icon: Clock, color: 'bg-yellow-100 text-yellow-800' },
  domium_attestation_definitive: { label: 'Attestation Définitive', icon: CheckCircle, color: 'bg-green-100 text-green-800' },
  domium_recu_paiement: { label: 'Reçu de Paiement', icon: Receipt, color: 'bg-emerald-100 text-emerald-800' },
  domium_pv_reception: { label: 'PV de Réception', icon: FileCheck, color: 'bg-purple-100 text-purple-800' },
  domium_facture_acquittee: { label: 'Facture Acquittée', icon: FileCheck, color: 'bg-indigo-100 text-indigo-800' },
  domium_preuve_paiement: { label: 'Preuve de Paiement', icon: FileCheck, color: 'bg-teal-100 text-teal-800' },
};

export default function GEDDomium({ subscriptionReference = null }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [viewerDoc, setViewerDoc] = useState(null);
  
  const token = getToken();
  const { previewDocument: previewDocumentBlob } = useDocumentManager(token);
  
  // Charger les documents DOMIUM
  const fetchDocuments = useCallback(async () => {
    const authToken = getToken();
    if (!authToken) return;
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/domium/subscribe/ged/all`;
      if (subscriptionReference) {
        url = `${BACKEND_URL}/api/domium/subscribe/${subscriptionReference}/ged-documents`;
      }
      
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${authToken}` } });
      
      if (response.data.success) {
        setDocuments(response.data.documents || []);
      } else {
        toast.error('Erreur lors du chargement des documents');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setDocuments([]);
      } else {
        toast.error('Erreur de connexion a la GED');
      }
    } finally {
      setLoading(false);
    }
  }, [subscriptionReference]);
  
  // Synchroniser les documents vers la GED
  const handleSync = async () => {
    if (!subscriptionReference) {
      toast.error('Aucune souscription selectionnee');
      return;
    }
    
    setSyncing(true);
    try {
      const authToken = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/domium/subscribe/${subscriptionReference}/sync-ged`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      
      if (response.data.success) {
        toast.success(`${response.data.documents_synced} document(s) synchronise(s)`);
        fetchDocuments();
      } else {
        toast.error('Erreur lors de la synchronisation');
      }
    } catch {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };
  
  // Telecharger un document
  const handleDownload = (doc) => {
    const authToken = getToken();
    const docId = doc.id || doc.ged_id;
    const url = `${BACKEND_URL}/api/ged/download/${docId}?token=${encodeURIComponent(authToken)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || doc.filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // Visualiser un document via la modale pro
  const handleView = (doc) => {
    setViewerDoc({
      id: doc.id || doc.ged_id,
      original_filename: doc.original_filename || doc.filename,
      filename: doc.filename,
      mime_type: doc.mime_type,
      size: doc.file_size,
      source: 'DOMIUM',
      created_at: doc.created_at,
    });
  };

  const previewDomiumDoc = async (docId) => {
    const authToken = getToken();
    const url = `${BACKEND_URL}/api/ged/download/${docId}?token=${encodeURIComponent(authToken)}&mode=view`;
    return previewDocumentBlob(docId, url);
  };

  const downloadFromViewer = (doc) => {
    const authToken = getToken();
    const url = `${BACKEND_URL}/api/ged/download/${doc.id}?token=${encodeURIComponent(authToken)}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = doc.original_filename || doc.filename || 'document.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };
  
  // Filtrer les documents
  const filteredDocuments = documents.filter(doc => {
    const matchSearch = 
      doc.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.reference_domium_subscription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchCategory = filterCategory === 'all' || doc.categorie === filterCategory;
    
    return matchSearch && matchCategory;
  });
  
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);
  
  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Formater la taille du fichier
  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl flex items-center gap-2">
              <Shield className="h-5 w-5 text-emerald-600" />
              Documents DOMIUM - GED
            </CardTitle>
            <CardDescription>
              Gestion électronique des documents de souscription DOMIUM
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchDocuments}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            {subscriptionReference && (
              <Button 
                onClick={handleSync}
                disabled={syncing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <FolderSync className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                Synchroniser vers GED
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Filtres */}
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher un document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Filtrer par type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les documents</SelectItem>
              {Object.entries(DOMIUM_CATEGORIES).map(([key, { label }]) => (
                <SelectItem key={key} value={key}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Statistiques */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-6">
          {Object.entries(DOMIUM_CATEGORIES).slice(0, 5).map(([key, { label, icon: Icon, color }]) => {
            const count = documents.filter(d => d.categorie === key).length;
            return (
              <div key={key} className={`p-3 rounded-lg ${color.replace('text-', 'border-').replace('bg-', 'bg-opacity-50 bg-')}`}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{count}</span>
                </div>
                <div className="text-xs mt-1 opacity-80">{label}</div>
              </div>
            );
          })}
        </div>
        
        {/* Tableau des documents */}
        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto text-emerald-600" />
            <p className="mt-2 text-gray-500">Chargement des documents...</p>
          </div>
        ) : filteredDocuments.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <AlertCircle className="h-12 w-12 mx-auto text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">Aucun document DOMIUM</h3>
            <p className="mt-2 text-gray-500">
              {subscriptionReference 
                ? 'Cliquez sur "Synchroniser vers GED" pour générer les documents.'
                : 'Les documents apparaîtront ici après synchronisation des souscriptions.'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Fichier</TableHead>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Taille</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredDocuments.map((doc) => {
                const catInfo = DOMIUM_CATEGORIES[doc.categorie] || { 
                  label: doc.categorie, 
                  icon: FileText, 
                  color: 'bg-gray-100 text-gray-800' 
                };
                const Icon = catInfo.icon;
                
                return (
                  <TableRow key={doc.id}>
                    <TableCell>
                      <Badge className={catInfo.color}>
                        <Icon className="h-3 w-3 mr-1" />
                        {catInfo.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {doc.reference_domium_subscription}
                    </TableCell>
                    <TableCell className="max-w-48 truncate" title={doc.filename}>
                      {doc.filename}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <span className="font-medium">{[doc.user_prenom, doc.user_nom].filter(Boolean).join(' ') || '-'}</span>
                        {doc.user_espace && (
                          <div className="text-[10px] text-blue-600 bg-blue-50 inline-block px-1.5 rounded ml-1">{doc.user_espace}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{formatSize(doc.file_size)}</TableCell>
                    <TableCell>{formatDate(doc.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant={doc.statut === 'valide' ? 'default' : 'secondary'}>
                        {doc.statut === 'valide' ? (
                          <><CheckCircle className="h-3 w-3 mr-1" /> Validé</>
                        ) : doc.statut}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setPreviewDoc(doc)}
                          title="Détails"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm"
                          variant="outline"
                          className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                          onClick={() => handleView(doc)}
                          title="Voir le document"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={() => handleDownload(doc)}
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      
      {/* Modal de prévisualisation in-app pro (iframe + zoom + print + download) */}
      {viewerDoc && (
        <GEDDocumentViewerModal
          doc={viewerDoc}
          onClose={() => setViewerDoc(null)}
          onDownload={downloadFromViewer}
          previewDocument={previewDomiumDoc}
        />
      )}

      {/* Dialog de prévisualisation */}
      <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Détails du document</DialogTitle>
            <DialogDescription>
              Informations sur le document DOMIUM
            </DialogDescription>
          </DialogHeader>
          {previewDoc && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Type</label>
                  <p className="font-medium">
                    {DOMIUM_CATEGORIES[previewDoc.categorie]?.label || previewDoc.categorie}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Référence souscription</label>
                  <p className="font-mono">{previewDoc.reference_domium_subscription}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Fichier</label>
                  <p className="truncate">{previewDoc.filename}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Taille</label>
                  <p>{formatSize(previewDoc.file_size)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Date de création</label>
                  <p>{formatDate(previewDoc.created_at)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Statut</label>
                  <Badge variant={previewDoc.statut === 'valide' ? 'default' : 'secondary'}>
                    {previewDoc.statut}
                  </Badge>
                </div>
              </div>
              {previewDoc.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p>{previewDoc.description}</p>
                </div>
              )}
              {previewDoc.tags && previewDoc.tags.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Tags</label>
                  <div className="flex gap-2 mt-1">
                    {previewDoc.tags.map((tag, i) => (
                      <Badge key={i} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => handleView(previewDoc)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Voir
                </Button>
                <Button onClick={() => handleDownload(previewDoc)}>
                  <Download className="h-4 w-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
